import React, { useEffect, useMemo, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { format } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrash, faXmark, faCheck } from "@fortawesome/free-solid-svg-icons";
import { API } from "../../data/API/API";
import './InternalCommentsChatInline.scss'
type TargetType = "review" | "sending" | 'review-phone-review';

type InternalComment = {
  id: number;
  text: string;
  created: string;
  createdBy: string;
};

type Props = {
  targetType: TargetType;
  targetId: number;

  // Viktigt för höger/vänster-placering (samma format som backend skriver i CreatedBy)
  currentUserKey: string;

  title?: string;
  onCreated?: () => void;
};

const InternalCommentsChatInline: React.FC<Props> = ({
  targetType,
  targetId,
  currentUserKey,
  title,
  onCreated,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [comments, setComments] = useState<InternalComment[]>([]);
  const [newComment, setNewComment] = useState("");

  // edit state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState("");

  // confirm state
  const [showConfirmSaveEdit, setShowConfirmSaveEdit] = useState(false);
  const [pendingEditId, setPendingEditId] = useState<number | null>(null);

  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // const headerTitle = useMemo(() => {
  //   if (title) return title;
  //   return targetType === "review"
  //     ? `Interna kommentarer (Granskning ${targetId})`
  //     : `Interna kommentarer (Utskick ${targetId})`;
  // }, [title, targetType, targetId]);
const headerTitle = useMemo(() => {
  if (title) return title;

  if (targetType === "review") {
    return `Interna kommentarer (Granskning ${targetId})`;
  }

  if (targetType === "sending") {
    return `Interna kommentarer (Utskick ${targetId})`;
  }

  return `Interna kommentarer (Telefongranskning ${targetId})`;
}, [title, targetType, targetId]);
  const sortedComments = useMemo(() => {
    // chat: äldst -> nyast
    return [...comments].sort((a, b) => {
      const da = new Date(a.created).getTime();
      const db = new Date(b.created).getTime();
      return (isNaN(da) ? 0 : da) - (isNaN(db) ? 0 : db);
    });
  }, [comments]);

  // const fetchComments = async () => {
  //   setLoading(true);
  //   try {
  //     const data =
  //       targetType === "review"
  //         ? await API.InternalComments.getForReview(targetId)
  //         : await API.InternalComments.getForSending(targetId);

  //     setComments(data ?? []);
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  const fetchComments = async () => {
  setLoading(true);
  try {
    const data =
      targetType === "review"
        ? await API.InternalComments.getForReview(targetId)
        : targetType === "sending"
        ? await API.InternalComments.getForSending(targetId)
        : await API.InternalComments.getForReviewPhoneReview(targetId);

    setComments(data ?? []);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    // inline => alltid “aktiv”
    setNewComment("");
    setEditingId(null);
    setEditText("");
    setShowConfirmSaveEdit(false);
    setPendingEditId(null);
    setShowConfirmDelete(false);
    setPendingDeleteId(null);
    setUpdatingId(null);
    setDeletingId(null);

    fetchComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetType, targetId]);


  const handleCreate = async () => {
  const trimmed = newComment.trim();
  if (!trimmed) return;

  setSaving(true);
  try {
    const created =
      targetType === "review"
        ? await API.InternalComments.createForReview(targetId, trimmed)
        : targetType === "sending"
        ? await API.InternalComments.createForSending(targetId, trimmed)
        : await API.InternalComments.createForReviewPhoneReview(targetId, trimmed);

    if (created) {
      setComments((prev) => [...prev, created]);
      setNewComment("");
      onCreated?.();
    }
  } finally {
    setSaving(false);
  }
};

  const startEdit = (commentId: number, currentText: string) => {
    setEditingId(commentId);
    setEditText(currentText);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };

  const requestSaveEdit = (commentId: number) => {
    if (!editText.trim()) return;
    setPendingEditId(commentId);
    setShowConfirmSaveEdit(true);
  };

  const confirmSaveEdit = async () => {
    if (pendingEditId == null) return;
    const commentId = pendingEditId;
    const trimmed = editText.trim();
    if (!trimmed) return;

    setUpdatingId(commentId);
    try {
      const updated = await API.InternalComments.update(commentId, trimmed);
      if (updated) {
        setComments((prev) => prev.map((c) => (c.id === commentId ? { ...c, text: updated.text } : c)));
        setEditingId(null);
        setEditText("");
      }
    } finally {
      setUpdatingId(null);
      setShowConfirmSaveEdit(false);
      setPendingEditId(null);
    }
  };

  const requestDelete = (commentId: number) => {
    setPendingDeleteId(commentId);
    setShowConfirmDelete(true);
  };

  const confirmDelete = async () => {
    if (pendingDeleteId == null) return;
    const commentId = pendingDeleteId;

    setDeletingId(commentId);
    try {
      const deleted = await API.InternalComments.remove(commentId);
      if (deleted) {
        setComments((prev) => prev.filter((c) => c.id !== commentId));
        if (editingId === commentId) cancelEdit();
      }
    } finally {
      setDeletingId(null);
      setShowConfirmDelete(false);
      setPendingDeleteId(null);
    }
  };

  const isBusy = (id: number) => updatingId === id || deletingId === id;

  return (
    <>
      <div className="ic-chat">
  {/* <div className="ic-chat__header">
    <div className="ic-chat__title">{headerTitle}</div>
  </div> */}

  <div className="ic-chat__messages">
    {loading ? (
      <div className="ic-chat__loading">
        <Spinner animation="border" role="status" />
      </div>
    ) : sortedComments.length === 0 ? (
      <div className="ic-chat__empty">Inga interna kommentarer ännu.</div>
    ) : (
      sortedComments.map((c) => {
        const mine = c.createdBy === currentUserKey;
        const isEditing = editingId === c.id;

        const d = new Date(c.created);
        const timeStr = isNaN(d.getTime()) ? "" : format(d, "HH:mm");

        return (
          <div
            key={c.id}
            className={`ic-chat__row ${mine ? "ic-chat__row--mine" : ""}`}
          >
            <div
              className={`ic-chat__bubble ${
                mine ? "ic-chat__bubble--mine" : ""
              }`}
            >
              <div className="ic-chat__bubble-content">
                <p className="ic-chat__author">{c.createdBy}</p>

                {isEditing ? (
                  <textarea
                    className="form-control ic-chat__edit"
                    rows={3}
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    disabled={isBusy(c.id)}
                  />
                ) : (
                  <p className="ic-chat__text">{c.text}</p>
                )}

                <p className="ic-chat__time">{timeStr}</p>
              </div>

              <div className="ic-chat__actions">
                {isEditing ? (
                  <>
                    <button onClick={cancelEdit}>
                      <FontAwesomeIcon icon={faXmark} />
                    </button>
                    <button onClick={() => requestSaveEdit(c.id)}>
                      <FontAwesomeIcon icon={faCheck} />
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => startEdit(c.id, c.text)}>
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                    <button onClick={() => requestDelete(c.id)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })
    )}
  </div>

  <div className="ic-chat__input">
    <input
      placeholder="Skriv ett meddelande..."
      value={newComment}
      onChange={(e) => setNewComment(e.target.value)}
      onKeyDown={(e) => e.key === "Enter" && handleCreate()}
      disabled={saving}
    />
    <button onClick={handleCreate} disabled={saving || !newComment.trim()}>
      ➜
    </button>
  </div>
</div>

      {/* Bekräfta edit */}
      <Modal show={showConfirmSaveEdit} centered onHide={() => setShowConfirmSaveEdit(false)}>
        <Modal.Header closeButton style={{backgroundColor:'#173c53',color:'white'}}>
          <Modal.Title>Bekräfta ändring</Modal.Title>
        </Modal.Header>
        <Modal.Body 
style={{backgroundColor:'#fffaf5'}}>Är du säker på att du vill spara ändringen?</Modal.Body>
        <Modal.Footer style={{backgroundColor:'#fffaf5'}}>
          <button
            className="btn button-cancel"
            onClick={() => {
              setShowConfirmSaveEdit(false);
              setPendingEditId(null);
            }}
            disabled={pendingEditId != null && updatingId === pendingEditId}
          >
            Avbryt
          </button>
          <button
            className="btn"
            onClick={confirmSaveEdit}
            disabled={pendingEditId == null || (pendingEditId != null && updatingId === pendingEditId)}
          >
            {pendingEditId != null && updatingId === pendingEditId ? "Sparar..." : "Bekräfta"}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Bekräfta delete */}
      <Modal show={showConfirmDelete} centered onHide={() => setShowConfirmDelete(false)}>
        <Modal.Header closeButton style={{backgroundColor:'#173c53',color:'white'}}>
          <Modal.Title>Bekräfta borttagning</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{backgroundColor:'#fffaf5'}}>Är du säker på att du vill ta bort kommentaren?</Modal.Body>
        <Modal.Footer style={{backgroundColor:'#fffaf5'}}>
          <button
            className="btn button-cancel"
            onClick={() => {
              setShowConfirmDelete(false);
              setPendingDeleteId(null);
            }}
            disabled={pendingDeleteId != null && deletingId === pendingDeleteId}
          >
            Avbryt
          </button>
          <button
            className="btn"
            onClick={confirmDelete}
            disabled={pendingDeleteId == null || (pendingDeleteId != null && deletingId === pendingDeleteId)}
          >
            {pendingDeleteId != null && deletingId === pendingDeleteId ? "Tar bort..." : "Bekräfta"}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default InternalCommentsChatInline;
